import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import {EmployeeView, RoutesResult} from 'planner-shared';
import {RouteTaskService} from '../../../route-planning/tasks/services/route-task.service';
import {RouteTaskView} from '../../../route-planning/tasks/route-task-view.model';
import {EmployeeService, NavigationService, OptimizationService, ProblemInstanceService, RoutesService, TravelTimeTransportService} from '../../../shared/services';
import {switchMap, take} from 'rxjs/operators';
import {RouteKeyNumberWeights} from "../../../shared/models/route-key-number-weights.model";
import {TimeService, TransportType} from "@shared";
import { TravelTimeTransport } from '../../../shared/models/travel-time-transport';
import { range } from 'rxjs';
import { RouteVisualizationService } from '../../../route-planning/tasks/services/route-visualization.service';

@Component({
    selector: 'app-route-comparison-solution',
    templateUrl: './route-comparison-solution.component.html',
    styleUrls: ['./route-comparison-solution.component.scss'],
    standalone: false
})
export class RouteComparisonSolutionComponent implements OnInit {
    private readonly routeTaskService = inject(RouteTaskService);
    private readonly employeeService = inject(EmployeeService);
    private readonly navigationService = inject(NavigationService);
    private readonly optimizationService = inject(OptimizationService);
    private readonly routesService = inject(RoutesService);
    private readonly timeService = inject(TimeService);
    private readonly travelTimeTransportService = inject(TravelTimeTransportService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly routeVisualizationService = inject(RouteVisualizationService);



    @Input('route')
    public set routes(routeResult: RoutesResult) {
        if (routeResult) {
            this.routeTaskService.mapToRouteTasks(routeResult.routes.tasks)
                .subscribe(routeTasks => {
                    this.routeTasks = routeTasks;
                    this.routeResult = routeResult;
                    this.loadWeights();
                    this.loadTasks();
                });

            this.travelTimeTransportService.mapToTravelTimeTransport(routeResult.routes.routeKeyNumbers)
                .subscribe(transportTravelTime => this.transportTravelTime = transportTravelTime);
            this.transportTooltip =
                this.travelTimeTransportService.formatTransportTooltip(this.transportTravelTime);

            this.loadTravelTimeVisualization();
        }
    }

    @Input() isRideSharingInRoute: boolean;

    @Input() isRideSharing: boolean;

    @Input() jobId: string;

    @Output() closeModal = new EventEmitter();

    public routeTasks: RouteTaskView[];
    public routeResult: RoutesResult;
    public employees: EmployeeView[];
    public currentEmployeeIndex = 0;
    private routeKeyNumberWeights: RouteKeyNumberWeights;
    public transportTravelTime: TravelTimeTransport;
    public transportTooltip: string;
    public rideShareIndexes: number[][];
    public transportTypes = TransportType;
    public officeAddress: string;

    ngOnInit(): void {
        this.employeeService.getEmployees()
            .subscribe(employees => this.employees = employees
                .filter(employee => employee.isActive));
        this.routeKeyNumberWeights = <RouteKeyNumberWeights>{
            accurateArrivals: [0, 0],
            travelTime: [0, 0],
            unallocatedTasks: 0
        };
        this.problemInstanceService.getProblemInstance()
            .subscribe(instance => {
                this.officeAddress = instance.problemInstance.officeInfo.streetAddressNameNumberAndLetter;
            });
    }

    public nextEmployee(): void {
        this.currentEmployeeIndex++;
        this.loadTasks();
    }

    public prevEmployee(): void {
        this.currentEmployeeIndex--;
        this.loadTasks();
    }

    public select(): void {
        this.optimizationService
            .stopOptimizationProcess()
            .pipe(
                switchMap(() => this.routesService.setRoutes(this.jobId))
            )
            .subscribe(() => {
                this.navigationService.navigateNext();
                this.closeModal.emit();
            });
    }

    private loadTasks(): void {
        const workHourId = this.routeResult.routes.routeKeyNumbers
            .filter(routeKey => routeKey.employeeId === this.employees[this.currentEmployeeIndex].employeeId)[0].workHourId;

        this.routeTaskService
            .mapToRouteTasks(this.routeResult.routes.tasks
                .filter(task => task.employeeId === this.employees[this.currentEmployeeIndex].employeeId && task.workHourId === workHourId))
            .subscribe(routeTasks => this.routeTasks = routeTasks);

        this.loadTravelTimeVisualization();
    }

    private loadWeights(): void {
        this.routeKeyNumberWeights.accurateArrivals[0] = this.timeService
            .extractHours(this.routeResult.routes.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.accurateArrivals[1] = this.timeService
            .extractMinutes(this.routeResult.routes.totalKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.travelTime[0] = this.timeService
            .extractHours(this.routeResult.routes.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.travelTime[1] = this.timeService
            .extractMinutes(this.routeResult.routes.totalKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.unallocatedTasksPatients
            = this.routeResult.routes.totalKeyNumbers.totalNumberOfNonAllocatedPatientTasks;
        this.routeKeyNumberWeights.unallocatedTasks
            = this.routeResult.routes.totalKeyNumbers.totalNumberOfNonAllocatedEmployeeTasks;
    }

    public getTaskFromAddress(taskIndex: number): string {
        return this.routeVisualizationService.getTaskFromAddress(taskIndex, this.routeTasks);
    }

    public getTaskToAddress(taskIndex): string {
        return this.routeVisualizationService.getTaskToAddress(taskIndex, this.routeTasks);
    }

    public getRideSharingCompanionsName(routeTask: RouteTaskView): string {
        return this.routeVisualizationService.getRideSharingCompanionsName(routeTask);
    }

    public showRideSharingTask(taskIndex: number) : boolean {
        if (!this.isRideSharing || !this.routeTasks.find(routeTask => routeTask.rideSharingConfig)) {
            return false;
        }

        let hasRideSharing = false;

        this.rideShareIndexes.forEach(rideShare => {
           if (rideShare.includes(taskIndex) && taskIndex !== rideShare[0]) {
               hasRideSharing = true;
           }
        });

        return hasRideSharing;
    }

    private loadTravelTimeVisualization() {
        if (this.routeTasks.find(routeTask => routeTask.rideSharingConfig)) {
            this.loadRideSharingRoutes();
            this.loadTravelTimeIcons();
        }
        this.routeTasks[0].showIcon = true;

        this.routeTasks = this.routeVisualizationService.loadRideSharingTasksDescription(this.routeTasks, false);
    }

    private loadTravelTimeIcons() {
        this.routeTasks.forEach((routeTask, index) => {
            this.rideShareIndexes.forEach(rideShare => {
                if (rideShare.includes(index)) {
                    if (rideShare[1] === index) {
                        routeTask.showIcon = true;
                        routeTask.rideSharingIcon = true;
                    }
                } else if (index !== 0 && rideShare.includes(index - 1)) {
                    routeTask.showIcon = true;
                }
            });
        });
    }

    private loadRideSharingRoutes() {
        let rideSharingCounter = 0;
        let startingIndex = 0;
        this.rideShareIndexes = [];
        this.routeTasks.forEach((task, arrayIndex) => {
            if (task.rideSharingConfig) {
                rideSharingCounter++;
                if (rideSharingCounter === 1) {
                    startingIndex = arrayIndex;
                } else if (rideSharingCounter === 2) {
                    rideSharingCounter = 0;
                    let indexCount = arrayIndex - startingIndex;
                    let rideShareIndexes: number[] = [];
                    range(startingIndex, indexCount + 1)
                        .pipe(
                            take(indexCount + 1))
                        .subscribe(x => {
                            rideShareIndexes.push(x);
                        });
                    this.rideShareIndexes.push(rideShareIndexes);
                }
            }
        });
    }
}
